<template>
  <div class="pumpMaintenanceRow grid grid-col-12 pb-8">
    <card>
      <template v-slot>
        <div class="flex item-start">
          <div class="flex-shrink-0"><v-pump></v-pump></div>
          <div class="flex-1">
            <div class="text-2xl font-semibold leading-6 flex-1 mx-2">
              {{ data.name }}
            </div>
          </div>
          <div class="flex-2">
            <div class="text-large leading-6 flex-1 mx-2 my-1">Onderhoud later inplannen</div>
          </div>
          <div class="flex-shrink-0 cursor-pointer ga-track-btn ga-track-plan_maintenance_later">
            <v-close @click="removePumpFromMaintenance(data.equipmentId)"></v-close>
          </div>
        </div>
        <div class="grid grid-cols-3 my-2">
          <div class="col-span-2">
            <div class="flex">
              <p class="text-black font-bold m-0 px-2 py-2">Serienummer:</p>
              <span class="px-1 py-2 text-black"> {{ data.serialNumber }}</span>
            </div>
            <div class="flex">
              <p class="text-black font-bold m-0 px-2 py-2">Locatie:</p>
              <span class="px-1 py-2 text-black">
                {{ data.location }}
                <span v-if="data.location === 'In depot'">
                  {{ data.defaultWarehouseAddress.postalCode }},
                  {{ data.defaultWarehouseAddress.city }}
                </span>
              </span>
            </div>
            <div class="flex">
              <p class="text-black font-bold m-0 px-2 py-2">Onderhoud nodig in:</p>
              <span class="px-1 py-2 text-black" v-if="data.nextMaintenanceDate !== null">
                {{ getMaintenanceDays(data.nextMaintenanceDate) }} dagen
              </span>
              <span class="px-1 py-2 text-black" v-else>Onbekend</span>
            </div>
            <div class="grid grid-cols-2 gap-4 my-2 w-10/12" v-if="data.location !== 'Bij patiënt'">
              <div>
                <p class="text-black font-bold m-0 px-2 py-2">Ophaallocatie*</p>
                <div class="flex px-2 py-2">
                  <input
                    :id="'depotAddress' + data.equipmentId"
                    :name="'pumpAddress' + data.equipmentId"
                    type="radio"
                    checked
                    class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600 ga-track-btn ga-track-pickup_location"
                    @change="checkRadioAddress(false, data.defaultWarehouseAddress.postalCode)"
                  />
                  <label
                    :for="'depotAddress' + data.equipmentId"
                    class="ml-3 block text-sm font-medium leading-6 text-gray-900"
                  >
                    In depot, {{ data.defaultWarehouseAddress.postalCode }},
                    {{ data.defaultWarehouseAddress.city }}
                  </label>
                </div>
                <div class="flex px-2 py-2">
                  <input
                    :id="'differentaddress' + data.equipmentId"
                    :name="'pumpAddress' + data.equipmentId"
                    type="radio"
                    class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600 ga-track-btn ga-track-pickup_location"
                    @change="checkRadioAddress(true, data.defaultWarehouseAddress.postalCode)"
                  />
                  <label
                    :for="'differentaddress' + data.equipmentId"
                    class="ml-3 block text-sm font-medium leading-6 text-gray-900"
                  >
                    Ander ophaaladres
                  </label>
                </div>
              </div>
              <div>
                <p class="text-black font-bold m-0 px-2 py-2">Ophaaldatum*</p>
                <v-input-date-picker
                  class="px-2 py-2 ga-track-btn ga-track-pickup_date"
                  id="pumpMaintenanceDate"
                  ref="date"
                  type="date"
                  name="pumpMaintenanceDate"
                  autofocus
                  disable-dates
                  disable-past-dates
                  :overwrite-value="maintenanceDate"
                  :disable-dates-for-pump-maintenance="data.nextMaintenanceDate ? data.nextMaintenanceDate : null"
                  :disable-dates-for-pump-pickup="disableDatesForPickUp"
                  :smallest-date="smallestDate"
                  :dates-by-postalcode="differentPickupDates"
                  @update-validation="updatePumpMaintenanceDate"
                  disable-current-date-highlight
                />
              </div>
            </div>
            <div class="grid grid-rows-1 my-2 w-10/12" v-else>
              <warning-message
                warning="Let op, deze pomp moet eerst worden gewisseld"
                :show-close="true"
                :html="true"
                :is-urgent="false"
                pump-message
                @patient="goToPatientDashboard(data.customerId)"
              />
            </div>
            <div class="grid grid-rows-1 my-2 w-1/3" v-if="differentAddress">
              <div class="grid grid-cols-2 gap-2">
                <div class="flex items-center px-2 py-2 col">
                  <div class="col w-full">
                    <v-input-group-simple-2
                      :id="'Postcode' + data.equipmentId"
                      v-model:value="addressDetails.postalCode"
                      type="text"
                      maxlength="7"
                      :rules="{
                        required: true,
                        regex: /^[1-9][0-9]{3} ?(?!sa|sd|ss)[a-z]{2}$/i
                      }"
                      label="Postcode"
                      @blur="autoFillAddress()"
                      @input="updateAddressDetails('postalCode', $event)"
                    />
                  </div>
                </div>
                <div class="flex items-center px-2 py-2 col">
                  <div class="col w-full">
                    <v-input-group-simple-2
                      :id="'Huisnummer' + data.equipmentId"
                      v-model:value="addressDetails.houseNumber"
                      type="text"
                      :rules="{
                        required: true
                      }"
                      label="Huisnummer"
                      @blur="autoFillAddress()"
                      @input="updateAddressDetails('houseNumber', $event)"
                    />
                  </div>
                </div>
                <div class="flex items-center px-2 py-2 col-span-2">
                  <div class="w-full">
                    <v-input-group-simple-2
                      :id="'Straatnaam' + data.equipmentId"
                      v-model:value="addressDetails.streetName"
                      type="text"
                      :rules="{
                        required: true
                      }"
                      label="Straatnaam"
                      :disabled="addressLookupSuccessful"
                      @input="updateAddressDetails('streetName', $event)"
                    />
                  </div>
                </div>
                <div class="flex items-center px-2 py-2 col-span-2">
                  <div class="w-full">
                    <v-input-group-simple-2
                      :id="'Woonplaats' + data.equipmentId"
                      v-model:value="addressDetails.city"
                      type="text"
                      :rules="{
                        required: true
                      }"
                      label="Woonplaats"
                      :disabled="addressLookupSuccessful"
                      @input="updateAddressDetails('city', $event)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div>
            <img
              class="mx-auto h-32 w-32 flex-shrink-0 rounded-full border-solid border-2 border-neutral-400 my-8"
              :src="data.imageUrl"
            />
          </div>
        </div>
      </template>
    </card>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import moment from 'moment';
import router from '@/router';
import { ref, watch, inject } from 'vue';
import VPump from 'icons/GasStationOutline';
import VClose from 'icons/WindowClose';
import WarningMessage from '@/components/shared/WarningMessage';
import VInputDatePicker from '@/components/shared/form/simple/InputDatePicker';
import { useStore } from 'vuex';
import { useForm } from 'vee-validate';
import { localDateToJson, stringToLocalDate } from '../../../services/date.service';
import { data } from 'jquery';

export default {
  name: 'PumpMaintenanceRow',
  components: { VPump, WarningMessage, VInputDatePicker, VClose },

  props: {
    data: {
      type: Object,
      default: () => {}
    },
    commonPumpMaintenanceDate: {
      type: String,
      required: false
    }
  },
  setup(props, { emit }) {
    const store = useStore();
    const disableDatesForPickUp = inject('disableDatesForPickUp');
    const smallestDate = inject('smallestDate');
    const differentPickupDates = inject('differentPickupDates');
    const { validate } = useForm();
    const addressDetails = ref({
      postalCode: '',
      houseNumber: '',
      streetName: '',
      city: ''
    });
    const fetchAddressLookup = (value) => store.dispatch('patient/fetchAddressLookup', value);
    const getMaintenanceDays = (nextMaintenanceDate) => {
      const differenceInMilliseconds = new Date(nextMaintenanceDate) - new Date();
      return Math.round(differenceInMilliseconds / (1000 * 60 * 60 * 24));
    };
    const differentAddress = ref(false);
    const maintenanceDate = ref('');
    const isInitializing = ref(false);
    maintenanceDate.value = smallestDate;
    const differentPostalCode = ref(null);

    const goToPatientDashboard = (patientId) => {
      store.dispatch('patient/setActivePatient', patientId).then(() => {
        router.push('/patientdashboard');
      });
    };

    const updatePumpMaintenanceDate = (value) => {
      maintenanceDate.value = value;
    };

    const checkRadioAddress = (status, postalCode) => {
      differentAddress.value = status;
      if (status == false) {
        differentPostalCode.value = postalCode;
      } else {
        addressDetails.value.postalCode = '';
        addressDetails.value.houseNumber = '';
        addressDetails.value.city = '';
        addressDetails.value.streetName = '';
      }
    };

    const autoFillAddress = () => {
      if (addressDetails.value.postalCode && addressDetails.value.houseNumber) {
        differentPostalCode.value = addressDetails.value.postalCode;
        fetchAddressLookup({
          postcode: addressDetails.value.postalCode,
          huisnummer: addressDetails.value.houseNumber,
          land: 'NL'
        }).then((response) => {
          addressDetails.value.city = response.data.City;
          addressDetails.value.streetName = response.data.Street;
        });
      }
    };

    const formatPostalCode = (value) => {
      return value ? value.trim().replace(/(\d+)\s*([A-Za-z]+)/, (match, p1, p2) => `${p1} ${p2.toUpperCase()}`) : '';
    };

    const updateAddressDetails = (key, event) => {
      addressDetails.value[key] = event.target.value;
      if (key == 'postalCode') {
        const formattedPostalCode = formatPostalCode(event.target.value);
        differentPostalCode.value = formattedPostalCode;
        addressDetails.value[key] = formattedPostalCode;
      }
    };

    const removePumpFromMaintenance = (equipmentId) => {
      emit('remove', equipmentId);
    };

    const validateAllFields = async () => {
      const result = await validate();
      const d = stringToLocalDate(maintenanceDate.value);
      const dJson = localDateToJson(d);
      return {
        status: result.valid,
        equipmentId: props.data.equipmentId,
        plannedMaintenanceDate: dJson,
        defaultWarehouseAddressId: !differentAddress.value ? props.data.defaultWarehouseAddress.addressId : 0,
        alternateWarehouseAddress: differentAddress.value ? addressDetails.value : null,
        customerId: props.data.customerId
      };
    };

    watch(maintenanceDate, (oldVal, newVal) => {
      if (isInitializing.value) {
        if (oldVal !== newVal) {
          emit('update', maintenanceDate.value);
        }
      } else {
        isInitializing.value = true;
      }
    });

    watch(differentPostalCode, (newVal, oldVal) => {
      if (newVal !== oldVal) {
        emit('getDisableDatesForPostalcode', newVal);
      }
    });

    watch(
      () => props.commonPumpMaintenanceDate,
      (newVal, oldVal) => {
        if (newVal !== oldVal) {
          maintenanceDate.value = newVal;
        }
      }
    );

    return {
      validate,
      validateAllFields,
      emit,
      ...mapActions('patient', ['setActivePatient']),
      goToPatientDashboard,
      getMaintenanceDays,
      checkRadioAddress,
      autoFillAddress,
      updateAddressDetails,
      differentAddress,
      removePumpFromMaintenance,
      updatePumpMaintenanceDate,
      maintenanceDate,
      differentPostalCode,
      disableDatesForPickUp,
      addressDetails,
      smallestDate,
      differentPickupDates
    };
  }
};
</script>
<style lang="scss" scoped>
.pumpMaintenanceRow {
  color: $teal-darkest;
  :deep(.form-group-simple label) {
    color: $black;
    font-weight: 700;
    font-size: 16px;
  }
  .material-design-icon {
    fill: $teal-darkest;
  }
}
</style>
